import { apiClient } from "./authService";
import type { VideoPlaybackKind } from "../utils/videoPlayback";

export interface Video {
  _id: string;
  title: string;
  description: string;
  cloudflareVideoId: string;
  /** Which player this video needs — resolved server-side. */
  playbackKind?: VideoPlaybackKind;
  iframeUrl?: string;
  playbackUrl?: string;
  thumbnailUrl?: string;
  duration?: number;
  status: "pending" | "processing" | "ready" | "error";
  visibility: "public" | "members" | "draft";
  level?: "none" | "pre-recorded" | "beginner" | "advanced";
  isFree?: boolean;
  plan?: "free" | "basic" | "premium";
  isAccessible?: boolean;
  category?: string;
  tags: string[];
  createdBy?: {
    _id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface VideosResponse {
  videos: Video[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  userTier?: "free" | "basic" | "premium";
}

export interface VideoDetailResponse {
  video: Video;
  playbackKind?: VideoPlaybackKind;
  iframeUrl?: string;
  playbackUrl: string;
  thumbnailUrl: string;
}

/**
 * Public Videos API - for authenticated members to access videos
 */
export const videosService = {
  /**
   * Get all accessible videos for the authenticated user
   */
  async getVideos(params?: {
    page?: number;
    limit?: number;
    visibility?: string;
    level?: "none" | "pre-recorded" | "beginner" | "advanced";
    isFree?: boolean;
    tag?: string;
    search?: string;
    category?: string;
  }): Promise<VideosResponse> {
    const response = await apiClient.get("/videos", { params });
    return response.data;
  },

  /**
   * Get featured/public videos (no auth required)
   */
  async getFeaturedVideos(): Promise<{ videos: Video[] }> {
    const response = await apiClient.get("/videos/featured");
    return response.data;
  },

  /**
   * Get a single video with playback URL
   */
  async getVideo(id: string): Promise<VideoDetailResponse> {
    const response = await apiClient.get(`/videos/${id}`);
    return response.data;
  },
};

export default videosService;
